import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";

/**
 * Uygulama-içi bildirim çanı (GENEL) — üst barda okunmamış rozeti + açılır liste.
 * Tüm operasyon olaylarını gösterir (sipariş/üretim/QC/sevkiyat/stok/bakım/satınalma/İK/proje).
 * Tıklayınca bildirimin `linkPath`'ine gider. Tercihler: Ayarlar > Bildirimler.
 */

// eventKey → ikon (yalnız Icon.tsx'te var olan adlar). Bilinmeyen → "bell".
const EVENT_ICON: Record<string, string> = {
  order_created: "package", order_ready: "package",
  wo_completed: "checkCircle",
  qc_pending: "clock", qc_rejected: "alertTriangle",
  shipment_ready: "truck", shipment_confirmed: "truck",
  low_stock: "alertTriangle",
  fault_reported: "alertTriangle", maintenance_overdue: "wrench",
  po_received: "cart",
  leave_requested: "calendar",
  task_assigned: "user", task_unassigned: "user", task_status_changed: "refresh",
  task_completed: "checkCircle", task_commented: "activity", task_progress_changed: "chart",
  task_dated: "calendar", task_attached: "upload",
};

/** Göreli zaman — TR, kısa. Mutlak tarih title'da. */
function timeAgo(d: string | Date): string {
  const diff = Date.now() - new Date(d).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return new Date(d).toLocaleDateString("tr-TR");
}

export function NotificationBell() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const unreadQ = trpc.notification.unreadCount.useQuery(undefined, {
    refetchInterval: 30_000,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const listQ = trpc.notification.list.useQuery(
    { limit: 20 },
    { enabled: open, staleTime: 5_000 },
  );

  const invalidate = () => utils.notification.invalidate();
  const markRead = trpc.notification.markRead.useMutation({ onSuccess: invalidate });
  const markAllRead = trpc.notification.markAllRead.useMutation({ onSuccess: invalidate });

  const unread = unreadQ.data ?? 0;
  const items = listQ.data?.items ?? [];

  function openNotif(id: string, linkPath: string | null, wasUnread: boolean) {
    if (wasUnread) markRead.mutate({ id });
    setOpen(false);
    if (linkPath) navigate(linkPath);
  }

  return (
    <div className="notif-wrap">
      <button
        type="button"
        className="icon-btn notif-btn"
        aria-label={unread > 0 ? `Bildirimler (${unread} okunmamış)` : "Bildirimler"}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Bildirimler"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={18} />
        {unread > 0 ? (
          <span className="notif-badge" aria-hidden="true">{unread > 9 ? "9+" : unread}</span>
        ) : null}
      </button>

      {open ? (
        <>
          <button type="button" className="user-backdrop" aria-label="Bildirimleri kapat" onClick={() => setOpen(false)} />
          <div className="notif-menu" aria-label="Bildirimler">
            <div className="notif-head">
              <span className="ttl">Bildirimler</span>
              {unread > 0 ? (
                <button type="button" className="notif-allread" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
                  <Icon name="check" size={13} />Tümünü okundu işaretle
                </button>
              ) : null}
            </div>

            {listQ.isLoading ? (
              <div className="notif-empty">Yükleniyor…</div>
            ) : items.length === 0 ? (
              <div className="notif-empty">
                <Icon name="bell" size={22} />
                <p>Bildirim yok</p>
              </div>
            ) : (
              <ul className="notif-list">
                {items.map((n) => {
                  const isUnread = n.readAt === null;
                  const sub = n.body ?? n.actorName ?? null;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={`notif-item ${isUnread ? "unread" : ""}`}
                        onClick={() => openNotif(n.id, n.linkPath, isUnread)}
                      >
                        <span className="notif-ic" aria-hidden="true"><Icon name={EVENT_ICON[n.eventKey] ?? "bell"} size={15} /></span>
                        <span className="notif-body">
                          <span className="notif-text"><b>{n.title}</b></span>
                          {sub ? <span className="notif-task">{sub}</span> : null}
                          <span className="notif-time mono" title={new Date(n.createdAt).toLocaleString("tr-TR")}>
                            {timeAgo(n.createdAt)}
                          </span>
                        </span>
                        {isUnread ? <span className="notif-dot" aria-label="okunmadı" /> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
